<script>
import Layout from '@/components/layout/app-layout'
import appConfig from '@/app.config'
import PageHeader from '@/components/layout/page-header'
import moment from 'moment'
import { authComputed } from '@/state/helpers'

import TotalRevenueCard from './shared/total-revenue.card'
import MillingRevenueCard from './shared/milling-revenue.card'
import MillingQuantityCard from './shared/milling-quantity.card'
import TotalOrdersCard from './shared/total-orders.card'
import WeeklyMillingQtyCard from './shared/weekly-milling-qty.card'
import WeeklyMillingAvgCard from './shared/weekly-milling-avg.card'
import MonthlyMillingQtyCard from './shared/monthly-milling-qty.card.vue'
import MonthlyMillingAvgCard from './shared/monthly-milling-avg.card.vue'
import PeriodSalesPerCustomerCard from './shared/period-sales-per-customer.card'
import PeriodSalesPerGroupCard from './shared/period-sales-per-group.card'
import FoamTypeStatsCard from './shared/foam-type-stats.card.vue'

export default {
  name: 'stats.sales',
  page: {
    title: 'Sales Statistics',
    meta: [{ name: 'description', settings_content: appConfig.description }]
  },
  components: {
    Layout,
    PageHeader,
    TotalRevenueCard,
    TotalOrdersCard,
    MillingRevenueCard,
    MillingQuantityCard,
    WeeklyMillingQtyCard,
    WeeklyMillingAvgCard,
    MonthlyMillingQtyCard,
    MonthlyMillingAvgCard,
    PeriodSalesPerCustomerCard,
    PeriodSalesPerGroupCard,
    FoamTypeStatsCard
  },
  data () {
    return {
      isLoading: false,
      items: [
        {
          text: 'Statistics'
        },
        {
          text: 'Sales',
          active: true
        }
      ],
      year: moment().year(),
      minYear: 2016,
      periodType: 'year',
      periodCursor: moment().startOf('year').toISOString(),
      periodFormats: {
        isoWeek: '[Week #]w [-] DD/MM/yyyy',
        month: 'MMMM yyyy',
        year: 'yyyy'
      },
      periodOptions: [
        { value: 'isoWeek', labelKey: 'stats.sales.period_weekly' },
        { value: 'month', labelKey: 'stats.sales.period_monthly' },
        { value: 'year', labelKey: 'stats.sales.period_yearly' }
      ],
      breakdownTab: 'customer',
      foamScope: 'foam',
      foamTab: 'revenue',
      foamShowLegend: false
    }
  },
  computed: {
    ...authComputed,
    maxYear () {
      return moment().year()
    },
    isCurrentYear () {
      return this.year === this.maxYear
    },
    canGoPrevYear () {
      return this.year > this.minYear
    },
    canGoNextYear () {
      return this.year < this.maxYear
    },
    periodUnit () {
      return this.periodType === 'isoWeek' ? 'isoWeek' : this.periodType
    },
    periodStart () {
      return moment(this.periodCursor).startOf(this.periodUnit)
    },
    periodEnd () {
      const end = moment(this.periodStart).endOf(this.periodUnit)
      if (this.isCurrentYear && end.isAfter(moment())) return moment()
      return end
    },
    periodStartIso () {
      return this.periodStart.toISOString()
    },
    periodEndIso () {
      return this.periodEnd.toISOString()
    },
    currentPeriodLabel () {
      return this.periodStart.format(
        this.periodFormats[this.periodType] || 'yyyy'
      )
    },
    canGoPrevPeriod () {
      if (this.periodType === 'year') return this.canGoPrevYear
      const minStart = moment()
        .year(this.year)
        .startOf('year')
      return this.periodStart.isAfter(minStart)
    },
    canGoNextPeriod () {
      if (this.periodType === 'year') return this.canGoNextYear
      const maxEnd = this.isCurrentYear
        ? moment()
        : moment()
            .year(this.year)
            .endOf('year')
      return this.periodEnd.isBefore(maxEnd)
    },
    foamPanelTitle () {
      if (this.foamScope === 'foam_mill') {
        return this.$t('stats.sales.foam_mill_title')
      }
      if (this.foamScope === 'milling') {
        return this.$t('stats.sales.milling_title')
      }
      return this.$t('stats.sales.foam_title')
    }
  },
  watch: {
    year () {
      this.resetPeriodCursor()
    },
    periodType () {
      this.resetPeriodCursor()
    }
  },
  mounted () {
    this.resetPeriodCursor()
  },
  methods: {
    periodBase () {
      return this.isCurrentYear
        ? moment()
        : moment()
            .year(this.year)
            .endOf('year')
    },
    resetPeriodCursor () {
      this.periodCursor = moment(this.periodBase())
        .startOf(this.periodUnit)
        .toISOString()
    },
    prevYear () {
      if (this.canGoPrevYear) this.year -= 1
    },
    nextYear () {
      if (this.canGoNextYear) this.year += 1
    },
    prevPeriod () {
      if (!this.canGoPrevPeriod) return
      if (this.periodType === 'year') {
        this.prevYear()
        return
      }
      this.periodCursor = moment(this.periodStart)
        .subtract(1, this.periodUnit)
        .startOf(this.periodUnit)
        .toISOString()
    },
    nextPeriod () {
      if (!this.canGoNextPeriod) return
      if (this.periodType === 'year') {
        this.nextYear()
        return
      }
      this.periodCursor = moment(this.periodStart)
        .add(1, this.periodUnit)
        .startOf(this.periodUnit)
        .toISOString()
    },
    callRefresh (refName) {
      const ref = this.$refs[refName]
      if (ref && typeof ref.onRefresh === 'function') {
        return ref.onRefresh()
      }
      return Promise.resolve()
    },
    async onRefresh () {
      this.isLoading = true

      const jobs = [
        this.callRefresh('totalRevenue'),
        this.callRefresh('totalOrders'),
        this.callRefresh('millingRevenue'),
        this.callRefresh('millingQty'),
        this.callRefresh('periodCustomerSales'),
        this.callRefresh('periodSales'),
        this.callRefresh('foamRevenue'),
        this.callRefresh('foamQuantity'),
        this.callRefresh('foamMillRevenue'),
        this.callRefresh('foamMillQuantity'),
        this.callRefresh('millingRevenueChart'),
        this.callRefresh('millingQuantityChart')
      ]

      if (this.isCurrentYear && this.periodType === 'year') {
        jobs.push(
          this.callRefresh('monthlyMillingQty'),
          this.callRefresh('monthlyMillingAvg'),
          this.callRefresh('weeklyMillingQty'),
          this.callRefresh('weeklyMillingAvg')
        )
      }

      await Promise.all(jobs)
      this.isLoading = false
    }
  }
}
</script>

<template>
  <Layout>
    <PageHeader :title="$t('stats.sales.page_title')" :items="items" />

    <div class="portal-stats">
      <div class="portal-card portal-card--panel portal-card--header mb-2">
        <div class="portal-header-main">
          <div>
            <div class="portal-card__eyebrow">{{ $t('stats.eyebrow') }}</div>
            <div class="portal-card__panel-title">
              {{ $t('stats.sales.overview') }}
            </div>
          </div>
          <div class="portal-header-actions">
            <div class="stats-tabs">
              <button
                v-for="option in periodOptions"
                :key="option.value"
                type="button"
                class="stats-tabs__btn"
                :class="{ 'is-active': periodType === option.value }"
                @click="periodType = option.value"
              >
                {{ $t(option.labelKey) }}
              </button>
            </div>
            <div class="portal-year-nav">
              <button
                type="button"
                class="portal-year-nav__btn"
                :disabled="!canGoPrevYear"
                :title="$t('stats.year.previous')"
                @click="prevYear"
              >
                <i class="bx bx-caret-left"></i>
              </button>
              <span class="portal-year-nav__value">{{ year }}</span>
              <button
                type="button"
                class="portal-year-nav__btn"
                :disabled="!canGoNextYear"
                :title="$t('stats.year.next')"
                @click="nextYear"
              >
                <i class="bx bx-caret-right"></i>
              </button>
            </div>
            <button
              type="button"
              class="portal-refresh-btn"
              :disabled="isLoading"
              :title="$t('stats.sales.refresh')"
              @click="onRefresh"
            >
              <i class="bx bx-sync"></i>
            </button>
          </div>
        </div>
        <div
          v-if="periodType !== 'year'"
          class="portal-header-period-row"
        >
          <div class="portal-period-nav">
            <button
              type="button"
              class="portal-period-nav__btn"
              :disabled="!canGoPrevPeriod"
              :title="$t('stats.sales.period_previous')"
              @click="prevPeriod"
            >
              <i class="bx bx-chevrons-left"></i>
            </button>
            <span class="portal-period-nav__label">{{
              currentPeriodLabel
            }}</span>
            <button
              type="button"
              class="portal-period-nav__btn"
              :disabled="!canGoNextPeriod"
              :title="$t('stats.sales.period_next')"
              @click="nextPeriod"
            >
              <i class="bx bx-chevrons-right"></i>
            </button>
          </div>
        </div>
      </div>

      <div class="portal-grid portal-grid--metrics">
        <total-revenue-card
          ref="totalRevenue"
          :year="year"
          :start-date="periodStartIso"
          :end-date="periodEndIso"
          :period-type="periodType"
        />
        <total-orders-card
          ref="totalOrders"
          :year="year"
          :start-date="periodStartIso"
          :end-date="periodEndIso"
          :period-type="periodType"
        />
        <milling-revenue-card
          ref="millingRevenue"
          :year="year"
          :start-date="periodStartIso"
          :end-date="periodEndIso"
          :period-type="periodType"
        />
        <milling-quantity-card
          ref="millingQty"
          :year="year"
          :start-date="periodStartIso"
          :end-date="periodEndIso"
          :period-type="periodType"
        />
        <template v-if="isCurrentYear && periodType === 'year'">
          <monthly-milling-qty-card ref="monthlyMillingQty" :year="year" />
          <monthly-milling-avg-card ref="monthlyMillingAvg" :year="year" />
          <weekly-milling-qty-card ref="weeklyMillingQty" :year="year" />
          <weekly-milling-avg-card ref="weeklyMillingAvg" :year="year" />
        </template>
      </div>

      <div class="portal-card portal-card--panel mb-2">
        <div class="portal-card__eyebrow">
          {{ $t('stats.sales.foam_eyebrow') }}
        </div>
        <div class="portal-card__panel-title mb-1">
          {{ foamPanelTitle }}
        </div>
        <div class="stats-tabs__toolbar stats-tabs__toolbar--tight">
          <div class="stats-tabs stats-tabs--grouped">
            <div class="stats-tabs__group">
              <button
                type="button"
                class="stats-tabs__btn"
                :class="{ 'is-active': foamScope === 'foam' }"
                @click="foamScope = 'foam'"
              >
                {{ $t('stats.sales.tab_foam') }}
              </button>
              <button
                type="button"
                class="stats-tabs__btn"
                :class="{ 'is-active': foamScope === 'milling' }"
                @click="foamScope = 'milling'"
              >
                {{ $t('stats.sales.tab_milling') }}
              </button>
              <button
                type="button"
                class="stats-tabs__btn"
                :class="{ 'is-active': foamScope === 'foam_mill' }"
                @click="foamScope = 'foam_mill'"
              >
                {{ $t('stats.sales.tab_foam_mill') }}
              </button>
            </div>
            <div class="stats-tabs__group">
              <button
                type="button"
                class="stats-tabs__btn"
                :class="{ 'is-active': foamTab === 'revenue' }"
                @click="foamTab = 'revenue'"
              >
                <i class="bx bx-euro" aria-hidden="true"></i>
                {{ $t('stats.sales.tab_revenue') }}
              </button>
              <button
                type="button"
                class="stats-tabs__btn"
                :class="{ 'is-active': foamTab === 'quantity' }"
                @click="foamTab = 'quantity'"
              >
                <i class="bx bx-package" aria-hidden="true"></i>
                {{ $t('stats.sales.tab_quantity') }}
              </button>
            </div>
          </div>
          <button
            type="button"
            class="stats-charts__legend-btn"
            @click="foamShowLegend = !foamShowLegend"
          >
            <i class="mdi mdi-table" aria-hidden="true"></i>
            {{
              foamShowLegend
                ? $t('stats.charts.hide_legend')
                : $t('stats.charts.show_legend')
            }}
          </button>
        </div>

        <foam-type-stats-card
          v-show="foamScope === 'foam' && foamTab === 'revenue'"
          ref="foamRevenue"
          breakdown="foam"
          data_field="revenue"
          :year="year"
          :start-date="periodStartIso"
          :end-date="periodEndIso"
          :period-type="periodType"
          :show-legend="foamShowLegend"
        />
        <foam-type-stats-card
          v-show="foamScope === 'foam' && foamTab === 'quantity'"
          ref="foamQuantity"
          breakdown="foam"
          data_field="quantity"
          :year="year"
          :start-date="periodStartIso"
          :end-date="periodEndIso"
          :period-type="periodType"
          :show-legend="foamShowLegend"
        />
        <foam-type-stats-card
          v-show="foamScope === 'milling' && foamTab === 'revenue'"
          ref="millingRevenueChart"
          breakdown="milling"
          data_field="revenue"
          :year="year"
          :start-date="periodStartIso"
          :end-date="periodEndIso"
          :period-type="periodType"
          :show-legend="foamShowLegend"
        />
        <foam-type-stats-card
          v-show="foamScope === 'milling' && foamTab === 'quantity'"
          ref="millingQuantityChart"
          breakdown="milling"
          data_field="quantity"
          :year="year"
          :start-date="periodStartIso"
          :end-date="periodEndIso"
          :period-type="periodType"
          :show-legend="foamShowLegend"
        />
        <foam-type-stats-card
          v-show="foamScope === 'foam_mill' && foamTab === 'revenue'"
          ref="foamMillRevenue"
          breakdown="foam_mill"
          data_field="revenue"
          :year="year"
          :start-date="periodStartIso"
          :end-date="periodEndIso"
          :period-type="periodType"
          :show-legend="foamShowLegend"
        />
        <foam-type-stats-card
          v-show="foamScope === 'foam_mill' && foamTab === 'quantity'"
          ref="foamMillQuantity"
          breakdown="foam_mill"
          data_field="quantity"
          :year="year"
          :start-date="periodStartIso"
          :end-date="periodEndIso"
          :period-type="periodType"
          :show-legend="foamShowLegend"
        />
      </div>

      <div class="portal-card portal-card--panel mb-2">
        <div class="portal-card__eyebrow">
          {{ $t('stats.sales.breakdown_eyebrow') }}
        </div>
        <div class="portal-card__panel-title mb-2">
          {{ $t('stats.sales.breakdown_title') }}
        </div>
        <div class="stats-tabs__toolbar">
          <div class="stats-tabs">
            <button
              type="button"
              class="stats-tabs__btn"
              :class="{ 'is-active': breakdownTab === 'customer' }"
              @click="breakdownTab = 'customer'"
            >
              {{ $t('stats.sales.tab_customer') }}
            </button>
            <button
              type="button"
              class="stats-tabs__btn"
              :class="{ 'is-active': breakdownTab === 'category' }"
              @click="breakdownTab = 'category'"
            >
              {{ $t('stats.sales.tab_category') }}
            </button>
          </div>
        </div>
        <period-sales-per-customer-card
          v-show="breakdownTab === 'customer'"
          ref="periodCustomerSales"
          :year="year"
          :start-date="periodStartIso"
          :end-date="periodEndIso"
        />
        <period-sales-per-group-card
          v-show="breakdownTab === 'category'"
          ref="periodSales"
          :year="year"
          :start-date="periodStartIso"
          :end-date="periodEndIso"
        />
      </div>
    </div>
  </Layout>
</template>
